import basicForm from "./basicForm";
import { formatCurrency } from "./util/formatCurrency";
import { createBasisTheoryClient } from "./util/basisTheoryClient";
import { btElementStyle } from "./util/basisTheoryStyle";

// Surcharge state (hasPastDue, surchargeFetched, isLoadingSurcharge,
// totalToPay) lives on the parent autopaySurcharge component.
export default function autopayCardForm(opts = {}) {
    const basisTheoryEnabled = opts.basisTheoryEnabled === true;
    const basisTheorySessionUrl = opts.basisTheorySessionUrl || '';

    return {
        ...basicForm(),

        cardType: null,
        expirationMonth: null,
        expirationYear: null,
        acknowledgePastDue: false,

        formatCurrency,

        basisTheoryEnabled,
        basisTheorySessionUrl,
        btClient: null,
        btCardNumberElement: null,
        btCardToken: '',
        btTokenizingPromise: null,
        btTokenizationSeq: 0,
        btError: '',
        btCardInvalid: false,
        btCardEmpty: true,
        btCardValid: false,
        btCardFocused: false,

        setBtCardToken(token) {
            this.btCardToken = token;
            if (this.$refs.cardToken) {
                this.$refs.cardToken.value = token;
                this.$refs.cardToken.dispatchEvent(new Event('input', { bubbles: true }));
            }
        },

        async init() {
            if (!this.basisTheoryEnabled) {
                return;
            }

            try {
                this.btClient = await createBasisTheoryClient(this.basisTheorySessionUrl);

                this.btCardNumberElement = this.btClient.createElement('cardNumber', {
                    targetId: 'bt-card-number',
                    iconPosition: 'right',
                    // Explicit empty string overrides BT's default "Card
                    // Number" placeholder — the label above is enough.
                    placeholder: '',
                    style: btElementStyle,
                });

                // Subscribe before mount so any ready/change BT emits
                // during mount is captured.
                this.btCardNumberElement.on('focus', () => { this.btCardFocused = true; });
                this.btCardNumberElement.on('blur', () => { this.btCardFocused = false; });

                this.btCardNumberElement.on('change', (event) => {
                    const tokenizationSeq = ++this.btTokenizationSeq;
                    this.setBtCardToken('');
                    this.btCardEmpty = event.empty;
                    this.btCardValid = event.valid;
                    if (event.empty) {
                        this.btTokenizingPromise = null;
                        this.btCardInvalid = false;
                        this.validate();
                        return;
                    }
                    if (!event.valid) {
                        this.btTokenizingPromise = null;
                        this.btCardInvalid = true;
                        this.validate();
                        return;
                    }
                    this.btCardInvalid = false;
                    this.validate();

                    this.btTokenizingPromise = (async () => {
                        try {
                            // Equiant stores the card on its own side once
                            // enrollment succeeds, so the intent only has to
                            // survive this enrollment — no cvc, and nothing
                            // for BT to keep afterwards.
                            const tokenIntent = await this.btClient.tokenIntents.create({
                                type: 'card',
                                data: {
                                    number: this.btCardNumberElement,
                                },
                            });
                            if (tokenizationSeq !== this.btTokenizationSeq) {
                                return null;
                            }
                            this.setBtCardToken(tokenIntent.id);
                            this.btTokenizingPromise = null;
                            return tokenIntent.id;
                        } catch (err) {
                            if (tokenizationSeq !== this.btTokenizationSeq) {
                                return null;
                            }
                            this.btError = 'Failed to tokenize card — please re-enter the number.';
                            this.setBtCardToken('');
                            this.btTokenizingPromise = null;
                            console.error('Basis Theory tokenization failed', err);
                        }
                    })();
                });

                await this.btCardNumberElement.mount('#bt-card-number');
            } catch (error) {
                this.btError = 'Failed to initialize payment form — please reload the page.';
                console.error('Basis Theory initialization failed', error);
            }
        },

        validate() {
            let baseValid = 0 === this.$el.querySelectorAll('[required]:invalid').length;

            if (this.basisTheoryEnabled) {
                baseValid = baseValid && !this.btCardEmpty && this.btCardValid;
            }

            // hasPastDue, surchargeFetched, isLoadingSurcharge come from
            // the parent autopaySurcharge component's scope.
            if (this.hasPastDue) {
                this.isValid = baseValid &&
                               this.acknowledgePastDue &&
                               this.surchargeFetched &&
                               !this.isLoadingSurcharge;
            } else {
                this.isValid = baseValid;
            }
        },

        async submit(event) {
            // Delegate to basicForm().submit at every "proceed" path so
            // the inherited isSubmitted/form-submitted bookkeeping fires
            // alongside the native form submission.
            if (!this.basisTheoryEnabled) {
                return basicForm().submit.call(this);
            }

            if (!this.btCardNumberElement) {
                this.btError = this.btError || 'Payment form failed to load. Please refresh.';
                event.preventDefault();
                return;
            }

            // A fast click mid-tokenization awaits the in-flight call
            // rather than submitting with an empty token.
            if (this.btTokenizingPromise && !this.btCardToken) {
                event.preventDefault();
                await this.btTokenizingPromise;
                if (!this.btCardToken) {
                    return;
                }
                await basicForm().submit.call(this);
                this.$nextTick(() => { this.$refs.form.submit(); });
                return;
            }

            if (this.btCardToken) {
                return basicForm().submit.call(this);
            }

            // No token yet and nothing in flight — the user hasn't
            // entered a valid card. Native validation on the BT Element
            // surfaces the error.
            event.preventDefault();
            this.btCardInvalid = true;
            this.btError = this.btError || 'Please enter a valid card number.';
        },
    };
}
